'use client';

import { useCallback, useRef, useState } from 'react';
import { useSignUp } from '@lib/auth/hooks/use-signup';
import type { SignupWithPhoneValues } from '@lib/auth/schemas/signup-with-phone-schema';
import { clearSignupPhoneHandoff, storeSignupPhoneHandoff } from '@lib/auth/utils/signup-phone-handoff';
import { requiresSignupPhoneOtp } from '@lib/auth/utils/signup-phone-opt-in-path';
export function useSignupSubmit(postSignupHref: string) {
  const {
    signUp,
    isLoading: isSignUpLoading,
    error: signUpError
  } = useSignUp();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Two Sign Up clicks can land before React re-renders, so a
  // state-backed in-flight flag reads stale on the second call.
  // The second would reach Clerk, get `identifier_exists`, and
  // start a shell claim over a signup that already succeeded.
  // A ref keeps the check synchronous.
  const isSubmittingRef = useRef(false);
  const startSignup = useCallback(async (values: SignupWithPhoneValues) => {
    if (isSubmittingRef.current) {
      return {
        status: 'failed' as const
      };
    }
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    // Onboarding owns SMS verification; hand the number over
    // before signUp because finalize navigates away as soon
    // as the session activates.
    const phone = values.phone?.trim() ?? '';
    if (requiresSignupPhoneOtp(values) && phone) {
      storeSignupPhoneHandoff({
        phone,
        smsOptIn: true
      });
    } else {
      clearSignupPhoneHandoff();
    }
    try {
      const outcome = await signUp({
        email: values.email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName
      }, postSignupHref);

      // Failures stay on /signup; a shell claim ends on /home.
      // Neither reaches the onboarding phone step.
      if (outcome.status !== 'complete') {
        clearSignupPhoneHandoff();
      }
      return outcome;
    } catch (err) {
      clearSignupPhoneHandoff();
      throw err;
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [postSignupHref, signUp]);
  return {
    startSignup,
    isLoading: isSubmitting || isSignUpLoading,
    error: signUpError
  };
}